import { DeleteOutlined, EditOutlined, FolderAddOutlined } from '@ant-design/icons';
import { useNavigate } from '@tanstack/react-router';
import { Col, Divider, Dropdown, Empty, Layout, Row, Spin, Typography } from 'antd';
import { ItemType } from 'antd/lib/menu/interface';
import React, { useState } from 'react';

import { CreateFolderModal, DeleteFileModal, DeleteFolderModal, UploadFileModal } from './modals';
import { RenameFileModal } from './modals/RenameFileModal';
import { RenameFolderModal } from './modals/RenameFolderModal';

import { useGetFolderChildren, useUpdateFileOrder } from '@/api';
import { FileManager } from '@/components/document/FileManager';
import { FolderBreadcrumb } from '@/components/document/FolderBreadcrumb';
import { FolderList } from '@/components/document/FolderList';
import { usePermission } from '@/hooks/usePermission';
import { FileItemResponse } from '@/proxy';
import { Route } from '@/routes/documents/$';
import { showMessage } from '@/shared/utils';

const { Title } = Typography;

export function DocumentManager() {
  const { _splat: currentPath = '' } = Route.useParams();
  const navigate = useNavigate();
  const [deleteFolderPath, setDeleteFolderPath] = useState<string | undefined>(undefined);
  const [deleteFilePath, setDeleteFilePath] = useState<string | undefined>(undefined);
  const [isCreateFolderModalOpen, setIsCreateFolderModalOpen] = useState(false);
  const [isUploadFileModalOpen, setIsUploadFileModalOpen] = useState(false);
  const [isDeleteFolderModalOpen, setIsDeleteFolderModalOpen] = useState(false);
  const [isDeleteFileModalOpen, setIsDeleteFileModalOpen] = useState(false);
  const [renameFolderState, setRenameFolderState] = useState<{
    isOpen: boolean;
    path: string;
    name: string;
    isFromDropdown?: boolean;
  }>({
    isOpen: false,
    path: '',
    name: '',
    isFromDropdown: false,
  });
  const [renameFileState, setRenameFileState] = useState<{
    isOpen: boolean;
    path: string;
    name: string;
  }>({
    isOpen: false,
    path: '',
    name: '',
  });

  const { data, isLoading } = useGetFolderChildren(currentPath);
  const updateFileOrderMutation = useUpdateFileOrder();
  const { isMAAdmin } = usePermission();

  const showCreateFolderModal = () => {
    setIsCreateFolderModalOpen(true);
  };

  const showUploadFileModal = () => {
    setIsUploadFileModalOpen(true);
  };

  const handleUpdateFileOrder = (files: FileItemResponse[]) => {
    updateFileOrderMutation.mutate(
      { folderPath: currentPath, order: files.map((item) => item.name) },
      {
        onSuccess: () => {
          showMessage('success', 'Reihenfolge erfolgreich gespeichert');
        },
        onError: () => {
          showMessage('error', 'Fehler beim Speichern der Reihenfolge');
        },
      }
    );
  };

  const showDeleteFolderModal = (path: string) => {
    setDeleteFolderPath(path);
    setIsDeleteFolderModalOpen(true);
  };

  const showDeleteFileModal = (path: string) => {
    setDeleteFilePath(path);
    setIsDeleteFileModalOpen(true);
  };

  const showRenameFolderModal = (path: string, name: string, isFromDropdown = false) => {
    setRenameFolderState({
      isOpen: true,
      path,
      name,
      isFromDropdown,
    });
  };

  const showRenameFileModal = (path: string, name: string) => {
    setRenameFileState({
      isOpen: true,
      path,
      name,
    });
  };

  const dropdownItems: ItemType[] = [
    {
      key: 'newFolder',
      icon: <FolderAddOutlined />,
      label: 'Neuer Ordner',
      onClick: showCreateFolderModal,
    },
    {
      key: 'renameFolder',
      icon: <EditOutlined />,
      label: 'Ordner umbenennen',
      onClick: () => showRenameFolderModal(currentPath, currentPath.split('/').pop() || '', true),
      disabled: currentPath === '',
    },
    {
      key: 'deleteFolder',
      icon: <DeleteOutlined />,
      label: 'Ordner löschen',
      onClick: () => showDeleteFolderModal(currentPath),
      disabled: currentPath === '',
      danger: true,
    },
  ];

  return (
    <Layout>
      <Layout.Content style={{ padding: '32px 24px', overflow: 'auto', background: '#fafbfc' }}>
        <FolderBreadcrumb
          folderPath={currentPath}
          onFolderSelected={(path) => {
            void navigate({ to: `/documents/${path}` });
          }}
        />

        <Row justify='space-between' align='middle' style={{ marginBottom: 24 }}>
          <Col>
            <Title level={2} style={{ margin: 0 }}>
              {currentPath ? currentPath.split('/').pop() : 'Dokumente'}
            </Title>
          </Col>
          <Col>
            {isMAAdmin && (
              <Dropdown.Button
                menu={{ items: dropdownItems }}
                placement='bottomRight'
                trigger={['click']}
                onClick={showUploadFileModal}
              >
                Datei hochladen
              </Dropdown.Button>
            )}
          </Col>
        </Row>

        {isLoading && <Spin />}
        {data && (
          <>
            {!!data.files.length && (
              <FileManager
                files={data.files}
                onOpenDeleteFileModal={showDeleteFileModal}
                onOpenRenameFileModal={showRenameFileModal}
                onReorder={handleUpdateFileOrder}
              />
            )}
            {!!data.files.length && !!data.folders.length && <Divider />}
            {!!data.folders.length && (
              <FolderList
                folders={data.folders}
                onFolderOpen={(path) => {
                  void navigate({ to: `/documents/${path}` });
                }}
                onOpenRenameFolderModal={showRenameFolderModal}
              />
            )}
            {!data.files.length && !data.folders.length && <Empty />}
          </>
        )}

        <CreateFolderModal
          isOpen={isCreateFolderModalOpen}
          onCancel={() => setIsCreateFolderModalOpen(false)}
          onSuccess={() => setIsCreateFolderModalOpen(false)}
          currentPath={currentPath}
        />

        <UploadFileModal
          isOpen={isUploadFileModalOpen}
          onCancel={() => setIsUploadFileModalOpen(false)}
          onSuccess={() => setIsUploadFileModalOpen(false)}
          currentPath={currentPath}
        />

        <DeleteFolderModal
          isOpen={isDeleteFolderModalOpen}
          onCancel={() => {
            setIsDeleteFolderModalOpen(false);
            setDeleteFolderPath(undefined);
          }}
          onSuccess={() => {
            setIsDeleteFolderModalOpen(false);
            setDeleteFolderPath(undefined);
          }}
          folderPath={deleteFolderPath}
        />

        <DeleteFileModal
          isOpen={isDeleteFileModalOpen}
          onCancel={() => {
            setIsDeleteFileModalOpen(false);
            setDeleteFilePath(undefined);
          }}
          onSuccess={() => {
            setIsDeleteFileModalOpen(false);
            setDeleteFilePath(undefined);
          }}
          filePath={deleteFilePath}
        />

        <RenameFolderModal
          isOpen={renameFolderState.isOpen}
          onClose={() =>
            setRenameFolderState({ isOpen: false, path: '', name: '', isFromDropdown: false })
          }
          onSuccess={(newName) => {
            if (renameFolderState.isFromDropdown) {
              const parentPath = renameFolderState.path.split('/').slice(0, -1).join('/');
              const newPath = parentPath ? `${parentPath}/${newName}` : newName;
              void navigate({ to: `/documents/${newPath}` });
            }
            setRenameFolderState({ isOpen: false, path: '', name: '', isFromDropdown: false });
          }}
          folderPath={renameFolderState.path}
          currentName={renameFolderState.name}
        />

        <RenameFileModal
          isOpen={renameFileState.isOpen}
          onClose={() => setRenameFileState({ isOpen: false, path: '', name: '' })}
          onSuccess={() => setRenameFileState({ isOpen: false, path: '', name: '' })}
          filePath={renameFileState.path}
          currentName={renameFileState.name}
        />
      </Layout.Content>
    </Layout>
  );
}
